<template>
    <canvas id="renderCanvas" ref="renderCanvasHTML" touch-action="none"></canvas>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, useTemplateRef } from 'vue';
import { useStore } from '@/store';
import { storeToRefs } from 'pinia';
import { initializeWorld } from './AliaxisWorld';

// BabylonJS Imports
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Color3, Color4, Vector3 } from "@babylonjs/core/Maths/math";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { Animation } from "@babylonjs/core/Animations/animation";
import { CubicEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { WebGPUEngine } from '@babylonjs/core/Engines/webgpuEngine';
import { AssetsManager } from '@babylonjs/core/Misc/assetsManager';
import { ArcRotateCameraPointersInput } from '@babylonjs/core/Cameras/Inputs/arcRotateCameraPointersInput';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { ILoadingScreen } from '@babylonjs/core/Loading/loadingScreen';
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
// import { MotionBlurPostProcess } from "@babylonjs/core/PostProcesses/motionBlurPostProcess";
// import { GrainPostProcess } from '@babylonjs/core/PostProcesses/grainPostProcess';
// import "@babylonjs/core/Rendering/prePassRendererSceneComponent";
// import "@babylonjs/core/Engines";

// Types
declare global {
    interface Window {
        engine: Engine | WebGPUEngine;
        scene: Scene;
        assetsManager: AssetsManager;
    }
}

const store = useStore();
const { isMobile } = storeToRefs(store);

const renderCanvasHTML = useTemplateRef('renderCanvasHTML');

onMounted(() => {
    initialize();
});

const initialize = async () => {
    console.log("Initializing BabylonJS..."); 

    await initializeEngine();
    initializeScene();
    initializeCamera();
    initializeAssetsManager(); 
    initializePostProcessing();
    await initializeWorld();

    console.log("BabylonJS initialized");
};

const initializeEngine = async () => {
    const webGPUSupported = await WebGPUEngine.IsSupportedAsync;
    if (webGPUSupported) { // WebGPU
        window.engine = new WebGPUEngine(renderCanvasHTML.value!, { adaptToDeviceRatio: true, antialias: true });
        await (window.engine as WebGPUEngine).initAsync();
    } else { // Fallback to WebGL
        window.engine = new Engine(renderCanvasHTML.value!, true, undefined, true);
    }

    // Custom Loading Screen Logic
    const customLoadingScreen: ILoadingScreen = {
        loadingUIBackgroundColor: "#000000",
        loadingUIText: "",
        displayLoadingUI: () => {
            store.setLoadingscreenVisible(true);
        },
        hideLoadingUI: () => {
            setTimeout(() => {
                store.setLoadingscreenVisible(false);
            }, 500);
        },
    }

    window.engine.loadingScreen = customLoadingScreen;
    window.addEventListener("resize", onWindowResize);
};

const onWindowResize = () => {
    window.engine.resize();
};

const initializeScene = () => {
    window.scene = new Scene(window.engine);
    window.scene.clearColor = new Color4(182 / 255, 191 / 255, 229 / 255, 1);
};

const initializeAssetsManager = () => {
    window.assetsManager = new AssetsManager(window.scene);

    window.assetsManager.onTaskErrorObservable.add((task) => {
        console.log('Babylon Task failed', task.errorObject?.message, task.errorObject?.exception);
    });

    window.assetsManager.onProgress = (remainingCount, totalCount) => {
        const percent = (100 * (totalCount - remainingCount) / totalCount).toFixed(0) + "%";
        const el = document.getElementsByClassName("ls-text")[0];
        if(el) el.innerHTML = percent;
    };

    window.assetsManager.onFinish = () => {
        console.log("Assets loaded - Global Finish");
        optimizeScene();
        playIntroAnimation();
        startRenderLoop();

        // TODO Remove hack, edit this in Blender
        let material1 = window.scene.getMaterialByName("AccessJunctionBasis_SimpleMaterialMap_cloneForAnimation");
        material1!.backFaceCulling = false;
        let material2 = window.scene.getMaterialByName("InvertReducer_SimpleMaterialMap_cloneForAnimation");
        material2!.backFaceCulling = false;
    };
};

const initializeCamera = () => {
    const camera = new ArcRotateCamera("Camera", Math.PI * 0.7 - 0.1, Math.PI / 4 - 0.1, 29, new Vector3(0, -5, 0), window.scene);
    camera.invertRotation = false;
    camera.angularSensibilityX = 1000;
    camera.angularSensibilityY = 1000;
    camera.fov = 1;
    camera.minZ = 0.2;
    camera.maxZ = 150;
    camera.upperBetaLimit = 1.1;
    camera.inertia = 0.8;
    camera.radius = 30;
    camera.upperRadiusLimit = 50;
    camera.lowerRadiusLimit = 20;
    camera.panningSensibility = 0;

    if (isMobile.value){
        camera.radius = 80;
        camera.lowerRadiusLimit = 30;
        camera.upperRadiusLimit = 90;
    }

    // Set default camera stats in Store
    const stats = {
        radius: {
            upperLimit: camera.upperRadiusLimit,
            lowerLimit: camera.lowerRadiusLimit,
            value: camera.radius,
        },
        alpha: {
            upperLimit: camera.upperAlphaLimit,
            lowerLimit: camera.lowerAlphaLimit,
            value: camera.alpha + 0.1,
        },
        beta: {
            upperLimit: camera.upperBetaLimit,
            lowerLimit: camera.lowerBetaLimit,
            value: camera.beta + 0.1,
        },
        target: camera.target,
    };
    store.setDefaultCamStats(stats);
};

const optimizeScene = () => {
    console.log("Optimizing Scene...");

    // Adaptive Performance Logic (every 1s, check FPS and adjust resolution)
    let lastCheckTime = Date.now();
    window.scene.onBeforeRenderObservable.add(() => {
        // Skip hidden tab
        if (document.hidden) return;

        const now = Date.now();
        if (now - lastCheckTime < 1000) return;

        lastCheckTime = now;
        
        const fps = window.engine.getFps();
        const currentScale = window.engine.getHardwareScalingLevel();
        let newScale = currentScale;

        // Decrease quality if below 50 FPS
        if (fps < 50 && currentScale < 1.5) newScale = Math.min(currentScale + 0.1, 1.5);
        // Increase quality if above 58 FPS
        else if (fps > 58 && currentScale > 0.5) newScale = Math.max(currentScale - 0.1, 0.5);

        // Apply scale changes
        if (newScale !== currentScale) {
            console.log(`FPS: ${fps.toFixed(0)} - Scaling to: ${newScale.toFixed(2)}`);
            window.engine.setHardwareScalingLevel(newScale);
        }
    });

    // Mesh Optimization
    let movableMeshes = ["Deckel_Graben1", "Deckel_Graben2", "pin_plane", "clouds"];
    let pickableMeshes = ["pin_plane", "_target"];
    
    window.scene.meshes.forEach(mesh => {
        mesh.checkCollisions = false;
        mesh.doNotSyncBoundingInfo = true;

        const isMovable = movableMeshes.some(keyword => mesh.name.includes(keyword));
        if(!isMovable){
            mesh.freezeWorldMatrix();
        }
        
        const isPickable = pickableMeshes.some(keyword => mesh.name.includes(keyword));
        if(!isPickable){
            mesh.isPickable = false;
        }
        
        if (mesh.isVerticesDataPresent(VertexBuffer.UV2Kind)) {
            (mesh as Mesh).removeVerticesData(VertexBuffer.UV2Kind);
        }
    });

    // Material Optimization
    let updateMaterialMeshes = ["Graben", "house_1", "Haus_3", "LM4_Graeben"];
    window.scene.materials.forEach(material => {
        material.checkReadyOnEveryCall = false;
        material.backFaceCulling = true;
        material.needDepthPrePass = false;

        const needsMaterialUpdate = updateMaterialMeshes.some(keyword => material.name.includes(keyword));
        if(!needsMaterialUpdate){
            material.freeze();
        }
    }); 

    window.scene.renderTargetsEnabled = false;
}

const playIntroAnimation = () => {
    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    const camera = window.scene.activeCamera as ArcRotateCamera;
    let radiusTo = isMobile.value ? 70 : 31;

    Animation.CreateAndStartAnimation("introRadius", camera, "radius", 60, 240, camera.radius, radiusTo, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    Animation.CreateAndStartAnimation("introAlpha", camera, "alpha", 60, 240, camera.alpha, camera.alpha + 0.1, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    Animation.CreateAndStartAnimation("introBeta", camera, "beta", 60, 240, camera.beta, camera.beta + 0.1, Animation.ANIMATIONLOOPMODE_CONSTANT, ease, () => {
        camera.attachControl(renderCanvasHTML.value, true);
        
        // Inputs konfigurieren NACHDEM attachControl gerufen wurde
        if(camera.inputs.attached.pointers) {
            let input = camera.inputs.attached.pointers as ArcRotateCameraPointersInput;
            input.multiTouchPanAndZoom = false;
            input.pinchZoom = false;
        }
        if(camera.inputs.attached.mousewheel) {
            camera.inputs.attached.mousewheel.detachControl();
        }
    });
};

const initializePostProcessing = () => {
    // Init rendering pipeline
    const pipeline = new DefaultRenderingPipeline("default", true, window.scene, [window.scene.activeCamera as ArcRotateCamera]);

    // Apply anti-aliasing
    pipeline.fxaaEnabled = true;
    pipeline.samples = 4;
    if (isMobile.value) pipeline.samples = 2;

    // Add bloom effect
    pipeline.bloomEnabled = true;
    pipeline.bloomThreshold = 0.8;
    pipeline.bloomWeight = 0.1;

    // Enhance colors and contrast
    pipeline.imageProcessingEnabled = true;
    // pipeline.imageProcessing.toneMappingEnabled = true;
    // pipeline.imageProcessing.toneMappingType = 1; // ACES
    // pipeline.imageProcessing.contrast = 1.2;
    // pipeline.imageProcessing.exposure = 1.0;

    // Sharpen to counter FXAA blur
    pipeline.sharpenEnabled = true;
    pipeline.sharpen.edgeAmount = 0.3;
    pipeline.sharpen.colorAmount = 1;

    // const motionBlur = new MotionBlurPostProcess("motionBlur", window.scene, 1.0, window.scene.activeCamera);
    // motionBlur.motionStrength = 6;
    // motionBlur.motionBlurSamples = 16;
    // motionBlur.isObjectBased = false;
    // if (isMobile.value) motionBlur.dispose();

    // const grain = new GrainPostProcess("grain", 1.0, window.scene.activeCamera as ArcRotateCamera);
    // grain.intensity = 15; 
    // grain.animated = true;
    // if (isMobile.value) grain.dispose();

    // Depth of field
    // pipeline.depthOfFieldEnabled = true;
    // pipeline.depthOfField.focalLength = 50; 
    // pipeline.depthOfField.fStop = 2.8; 
    // pipeline.depthOfField.focusDistance = 4; 
    // if (isMobile.value) pipeline.depthOfFieldEnabled = false;

    // Raycast loop
    // let lastRaycastTime = Date.now();
    // let targetDist = 31000;
    // const ignoredMeshes = ["clouds"];
    // window.scene.onBeforeRenderObservable.add(() => {
    //     if (!pipeline.depthOfFieldEnabled) return;

    //     const now = Date.now();
    //     if (now - lastRaycastTime > 250) {
    //         lastRaycastTime = now;
            
    //         const centerX = window.engine.getRenderWidth() / 2;
    //         const centerY = window.engine.getRenderHeight() / 2;
    //         const pickResult = window.scene.pick(centerX, centerY, (mesh) => 
    //             mesh.isVisible && !ignoredMeshes.some(name => mesh.name.includes(name))
    //         );

    //         if (pickResult && pickResult.hit) targetDist = pickResult.distance * 1000;
    //     }

    //     // Smooth focus
    //     pipeline.depthOfField.focusDistance = Scalar.Lerp(pipeline.depthOfField.focusDistance, targetDist, 0.1);
    // });
};

const startRenderLoop = () => {
    window.engine.runRenderLoop(() => {
        if (window.scene && window.scene.activeCamera) {
            window.scene.render();
        }
    });
};

onUnmounted(() => {
    window.removeEventListener("resize", onWindowResize);
    if (window.engine) window.engine.dispose();
});
</script>

<style>
#renderCanvas {
    transition: 1s filter ease, .25s transform ease-in-out;
    width: 100%;
    height: 100%;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}
</style>